import { RenzoRestakeManagerAbi } from '../abis/renzo'
import { TotalSupplyAbi } from '../abis/shared'
import type { LstRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type LstReader } from './shared'

/**
 * Renzo ezETH reader.
 *
 * Two calls: `ezETH.totalSupply()` + `RestakeManager.calculateTVLs()`.
 * `calculateTVLs()` returns a 3-tuple; the third element is total
 * ETH-denominated TVL across all operator delegators. Exchange rate
 * is TVL / totalSupply (1e18-scaled).
 *
 * `calculateTVLs()` is a relatively heavy call — fine for a single
 * per-chain snapshot but don't loop it.
 *
 * Fallback: when `readerExtras.restakeManager` is absent (e.g. on a
 * chain without a registered manager), surface a 1:1 placeholder
 * exchange rate so the entry still produces non-zero state.
 */
export const readerRenzoEzEth = (entry: LstRegistryEntry): LstReader => {
  const restakeManager = entry.readerExtras?.restakeManager
  if (!restakeManager) {
    return {
      calls: [{ address: entry.address, name: 'totalSupply', params: [] }],
      abis: [TotalSupplyAbi],
      parse: ([supply]) => {
        const totalSupply = toBigInt(supply)
        if (totalSupply === undefined) return undefined
        return {
          totalAssets: totalSupply,
          totalSupply,
          exchangeRate: ONE_E18,
        }
      },
    }
  }
  return {
    calls: [
      { address: entry.address, name: 'totalSupply', params: [] },
      { address: restakeManager, name: 'calculateTVLs', params: [] },
    ],
    abis: [TotalSupplyAbi, RenzoRestakeManagerAbi],
    parse: ([supply, tvls]) => {
      const totalSupply = toBigInt(supply)
      if (totalSupply === undefined || totalSupply === 0n) return undefined
      const totalTvl =
        Array.isArray(tvls) && tvls.length >= 3 ? toBigInt(tvls[2]) : undefined
      if (totalTvl === undefined) return undefined
      return {
        totalAssets: totalTvl,
        totalSupply,
        exchangeRate: (totalTvl * ONE_E18) / totalSupply,
      }
    },
  }
}
